import { supabase } from '../../../services/supabase/client'
import type {
  Photo,
  PhotoInsert,
  PhotoUpdate,
  PhotoWithDetails,
  PhotoCaptureData,
  PhotoUploadOptions,
  PhotoValidationResult,
  PhotoValidationError,
} from '../types/photo'
import {
  DEFAULT_PHOTO_UPLOAD_OPTIONS,
  SUPPORTED_IMAGE_TYPES,
  MAX_PHOTO_SIZE_BYTES,
} from '../types/photo'
import { offlineStorageService } from '../../sync/services/offlineStorage'
import { buildSyncQueueItem } from '../../sync/services/syncQueue'
import { withTimeout, ONLINE_REQUEST_TIMEOUT_MS } from '../../sync/services/withTimeout'
import { sanitizeStorageFileName } from '../../sync/services/storageKey'
import { purgePhotoStorage } from '../../../services/storage/storageCleanup'
import { imageCompressionService } from './imageCompression'
import { exifService } from './exifExtraction'
import { v4 as uuidv4 } from 'uuid'

/**
 * Combine the server photo list with local copies: captures still awaiting
 * sync (ids the server cannot know yet — pending creates, plus legacy temp_
 * ids) are appended, rows with a queued delete are hidden, and server rows
 * with a blob-backed local copy keep their local_blob_url so they render
 * without the network. Local copies of photos deleted from another device
 * match neither set and drop out.
 */
const mergeServerWithLocal = (
  server: Photo[],
  local: PhotoWithDetails[],
  pendingCreateIds: Set<string>,
  pendingDeleteIds: Set<string>
): PhotoWithDetails[] => {
  const localById = new Map(local.map((photo) => [photo.id, photo]))
  const serverIds = new Set(server.map((photo) => photo.id))
  const merged: PhotoWithDetails[] = server
    .filter((photo) => !pendingDeleteIds.has(photo.id))
    .map((photo) => ({
      ...photo,
      sync_status: 'synced' as const,
      stored_locally: localById.has(photo.id),
      local_blob_url: localById.get(photo.id)?.local_blob_url,
    }))
  merged.push(
    ...local.filter(
      (photo) =>
        !serverIds.has(photo.id) && (pendingCreateIds.has(photo.id) || photo.id.startsWith('temp_'))
    )
  )
  return merged.sort((a, b) => (a.captured_at ?? '').localeCompare(b.captured_at ?? ''))
}

/** True for a file this browser is likely unable to decode at all. */
const isHeic = (file: File): boolean =>
  /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name)

/**
 * User-facing reason a capture could not be processed. HEIC gets its own
 * wording because the fix is something the user can actually do — the format
 * only decodes in Safari, and iOS can be told to shoot JPEG instead.
 */
function photoProcessingMessage(file: File): string {
  return isHeic(file)
    ? 'This photo is in HEIC format, which this browser cannot read. ' +
        'On iPhone, set Settings → Camera → Formats to "Most Compatible", ' +
        'or add the photo using Safari.'
    : 'This photo could not be processed and has not been saved. ' +
        'Try taking it again, or choose a different file.'
}

export class PhotoService {
  /**
   * Validate photo file
   */
  validatePhoto(file: File): PhotoValidationResult {
    const errors: PhotoValidationError[] = []

    // Check file type
    if (!(SUPPORTED_IMAGE_TYPES as readonly string[]).includes(file.type)) {
      errors.push('INVALID_FILE_TYPE')
    }

    // Check file size
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      errors.push('FILE_TOO_LARGE')
    }

    return {
      valid: errors.length === 0,
      errors,
    }
  }

  /**
   * Photo queue items by operation, for telling local copies apart:
   * a queued create means "awaiting sync", a queued delete means "hide the
   * server row until the delete replays".
   */
  private async getPhotoQueueState(): Promise<{ creates: Set<string>; deletes: Set<string> }> {
    const items = await offlineStorageService.getQueueItems()
    const creates = new Set<string>()
    const deletes = new Set<string>()
    for (const item of items) {
      if (item.entity !== 'photo') continue
      if (item.operation === 'create') creates.add(item.entityId)
      if (item.operation === 'delete') deletes.add(item.entityId)
    }
    return { creates, deletes }
  }

  /**
   * Drop local copies of photos the server no longer has (deleted from
   * another device) — without this they linger forever and re-inflate
   * counts. Only synced copies qualify; anything still awaiting its create
   * is untouchable (REL-01). Best-effort: a failure just retries next read.
   */
  private pruneStaleLocalCopies(
    server: Photo[],
    local: PhotoWithDetails[],
    pendingCreateIds: Set<string>
  ): void {
    const serverIds = new Set(server.map((photo) => photo.id))
    for (const photo of local) {
      if (serverIds.has(photo.id)) continue
      if (photo.id.startsWith('temp_') || pendingCreateIds.has(photo.id)) continue
      void offlineStorageService.deletePhoto(photo.id).catch((error: unknown) => {
        console.warn('Failed to prune stale local photo copy:', error)
      })
    }
  }

  /**
   * Get all photos for a job
   * OFFLINE-FIRST: local copies (including offline captures awaiting sync)
   * always load; the server list is merged in when reachable. Any fetch
   * failure falls back to local — navigator.onLine lies on spotty links.
   */
  async getPhotosByJobId(jobId: string): Promise<PhotoWithDetails[]> {
    const local = await offlineStorageService.getPhotosByJobId(jobId)

    if (navigator.onLine) {
      try {
        const { data, error } = await withTimeout(
          supabase
            .from('photos')
            .select('*')
            .eq('job_id', jobId)
            .order('captured_at', { ascending: true }),
          ONLINE_REQUEST_TIMEOUT_MS,
          'job photos fetch'
        )

        if (error) throw error
        const { creates, deletes } = await this.getPhotoQueueState()
        this.pruneStaleLocalCopies(data || [], local, creates)
        return mergeServerWithLocal(data || [], local, creates, deletes)
      } catch (error) {
        console.error('Failed to fetch job photos online, using local cache:', error)
      }
    }

    return local
  }

  /**
   * Get all photos for a node — same offline-first contract as
   * getPhotosByJobId
   */
  async getPhotosByNodeId(nodeId: string): Promise<PhotoWithDetails[]> {
    const local = await offlineStorageService.getPhotosByNodeId(nodeId)

    if (navigator.onLine) {
      try {
        const { data, error } = await withTimeout(
          supabase
            .from('photos')
            .select('*')
            .eq('node_id', nodeId)
            .order('captured_at', { ascending: true }),
          ONLINE_REQUEST_TIMEOUT_MS,
          'node photos fetch'
        )

        if (error) throw error
        const { creates, deletes } = await this.getPhotoQueueState()
        this.pruneStaleLocalCopies(data || [], local, creates)
        return mergeServerWithLocal(data || [], local, creates, deletes)
      } catch (error) {
        console.error('Failed to fetch node photos online, using local cache:', error)
      }
    }

    return local
  }

  /**
   * Capture and upload photo
   */
  async capturePhoto(
    captureData: PhotoCaptureData,
    options: PhotoUploadOptions = DEFAULT_PHOTO_UPLOAD_OPTIONS
  ): Promise<Photo> {
    // Validate photo
    const validation = this.validatePhoto(captureData.file)
    if (!validation.valid) {
      throw new Error(`Invalid photo: ${validation.errors.join(', ')}`)
    }

    // A page opened before a background sync remapped its job/node can
    // still hold a temp_ id — resolve through the durable alias so the
    // capture lands on the real row instead of stranding on a deleted one
    const jobId = await offlineStorageService.resolveId(captureData.job_id)
    const nodeId = captureData.node_id
      ? await offlineStorageService.resolveId(captureData.node_id)
      : null

    let processedFile = captureData.file
    let thumbnailPath: string | null = null

    // Extract EXIF data if requested
    let metadata = captureData.metadata
    if (options.extract_exif) {
      try {
        metadata = await exifService.extractExif(captureData.file)
      } catch (error) {
        console.error('Failed to extract EXIF:', error)
      }
    }

    // Compress image if requested.
    //
    // A failure here used to be swallowed and the ORIGINAL file uploaded
    // instead. That put raw iPhone HEICs in the bucket: Safari decodes HEIC
    // so compression succeeds there, while Chrome and Firefox cannot decode
    // it at all — so the capture "worked", and the photo was then
    // undisplayable for every other browser and unusable in a report.
    // Storing something the app cannot render is worse than not storing it,
    // so the capture now fails where the user can see it.
    if (options.compress) {
      try {
        const compressionResult = await imageCompressionService.compress(processedFile, {
          maxWidth: options.max_width,
          maxHeight: options.max_height,
          quality: options.quality,
        })
        processedFile = compressionResult.compressed_file
      } catch (error) {
        console.error('Failed to compress image:', error)
        throw new Error(photoProcessingMessage(captureData.file))
      }
    }

    // Generate thumbnail if requested
    if (options.generate_thumbnail) {
      try {
        const thumbnailResult = await imageCompressionService.generateThumbnail(
          processedFile,
          options.thumbnail_size || 200
        )
        // Thumbnail will be uploaded separately
        thumbnailPath = await this.uploadThumbnail(jobId, thumbnailResult.thumbnail_blob)
      } catch (error) {
        console.error('Failed to generate thumbnail:', error)
      }
    }

    // One storage key AND one id for every path — online upload, offline
    // record, queued replay. If the online attempt times out after the
    // server accepted it, the queued sync finds the uploaded file (409
    // tolerated) and the existing row (matched by key or primary key)
    // instead of duplicating the photo. The client-generated id also means
    // no temp_ re-keying: queued edits and deletes stay valid across sync.
    // File names go through sanitizeStorageFileName: raw desktop names
    // (spaces in screenshot names) are invalid storage-key characters and
    // 400 the upload; file_name keeps the original for display.
    const storagePath = `${jobId}/${uuidv4()}_${sanitizeStorageFileName(processedFile.name)}`
    const photoId = uuidv4()

    // Try to upload if online
    try {
      if (navigator.onLine) {
        // Upload to Supabase Storage — bounded, so a dead link that
        // navigator.onLine misreports (VPN, captive portal) falls through
        // to the offline store instead of hanging in "Uploading..."
        const { error: uploadError } = await withTimeout(
          supabase.storage.from('photos').upload(storagePath, processedFile, {
            contentType: processedFile.type,
            upsert: false,
          }),
          ONLINE_REQUEST_TIMEOUT_MS,
          'photo upload'
        )

        if (uploadError) throw uploadError

        // Create photo record in database
        const photoInsert: PhotoInsert = {
          id: photoId,
          job_id: jobId,
          node_id: nodeId,
          storage_path: storagePath,
          file_name: processedFile.name,
          file_size_bytes: processedFile.size,
          mime_type: processedFile.type,
          caption: captureData.caption || null,
          gps_latitude: metadata?.gps_latitude || captureData.gps_latitude || null,
          gps_longitude: metadata?.gps_longitude || captureData.gps_longitude || null,
          gps_altitude: metadata?.gps_altitude || null,
          device_make: metadata?.device_make || null,
          device_model: metadata?.device_model || null,
          thumbnail_path: thumbnailPath,
          captured_at: metadata?.date_time_original || new Date().toISOString(),
        }

        const { data: photoData, error: photoError } = await withTimeout(
          supabase.from('photos').insert(photoInsert).select().single(),
          ONLINE_REQUEST_TIMEOUT_MS,
          'photo record insert'
        )

        if (photoError) throw photoError

        // Keep a blob-backed local copy so the photo also renders offline.
        // Best-effort only: the photo is already on the server, so a cache
        // failure (storage quota) must not reroute a success into the
        // offline path and queue a duplicate create.
        try {
          await offlineStorageService.savePhoto(photoData, processedFile)
        } catch (cacheError) {
          console.warn('Local photo cache write failed:', cacheError)
        }

        return photoData
      }
    } catch (error) {
      console.error('Failed to upload photo online:', error)
    }

    // If offline or online upload failed, store locally under the same id
    // and key. A blob: URL here would die on reload and is useless as an
    // upload key — the image bytes live in PHOTO_BLOBS until synced.
    const localPhoto: Photo = {
      id: photoId,
      job_id: jobId,
      node_id: nodeId,
      storage_path: storagePath,
      file_name: processedFile.name,
      file_size_bytes: processedFile.size,
      mime_type: processedFile.type,
      caption: captureData.caption || null,
      gps_latitude: metadata?.gps_latitude || captureData.gps_latitude || null,
      gps_longitude: metadata?.gps_longitude || captureData.gps_longitude || null,
      gps_altitude: metadata?.gps_altitude || null,
      device_make: metadata?.device_make || null,
      device_model: metadata?.device_model || null,
      thumbnail_path: thumbnailPath,
      captured_at: metadata?.date_time_original || new Date().toISOString(),
      display_order: null,
      exif_orientation: null,
      height_px: null,
      width_px: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    // Store in IndexedDB (with the actual file blob) and enqueue for sync
    // in one transaction (REL-09)
    await offlineStorageService.savePhotoWithQueueItem(
      localPhoto,
      buildSyncQueueItem('create', 'photo', photoId, {
        ...localPhoto,
        file: processedFile, // Include file for later upload
      }),
      processedFile
    )

    return localPhoto
  }

  /**
   * Upload thumbnail to storage
   */
  private async uploadThumbnail(jobId: string, thumbnailBlob: Blob): Promise<string | null> {
    try {
      const thumbnailPath = `${jobId}/thumb_${uuidv4()}.jpg`
      const { data, error } = await withTimeout(
        supabase.storage.from('photos').upload(thumbnailPath, thumbnailBlob, {
          contentType: 'image/jpeg',
          upsert: false,
        }),
        ONLINE_REQUEST_TIMEOUT_MS,
        'thumbnail upload'
      )

      if (error) throw error
      return data.path
    } catch (error) {
      console.error('Failed to upload thumbnail:', error)
      return null
    }
  }

  /**
   * Update photo metadata
   */
  async updatePhoto(id: string, updates: PhotoUpdate): Promise<Photo> {
    // A photo whose create hasn't completed isn't on the server yet: fold
    // the edit into the queued create so the photo uploads with its final
    // metadata — a separate update keyed to an id the server doesn't know
    // would dead-letter.
    const queueItems = await offlineStorageService.getQueueItems()
    const createItem = queueItems.find(
      (item) => item.entity === 'photo' && item.operation === 'create' && item.entityId === id
    )
    if (createItem || id.startsWith('temp_')) {
      const existingPhoto = await offlineStorageService.getPhoto(id)
      if (!existingPhoto) {
        throw new Error('Photo not found')
      }
      const updatedPhoto: Photo = {
        ...existingPhoto,
        ...updates,
        updated_at: new Date().toISOString(),
      }

      if (createItem && createItem.status !== 'in_progress') {
        createItem.data = { ...(createItem.data as Record<string, unknown>), ...updates }
        await offlineStorageService.savePhotoWithQueueItem(updatedPhoto, createItem)
      } else {
        // The create is mid-flight (or a legacy temp_ photo lost its create
        // item): queue a separate update — the queue replays it after the
        // create, and a temp-keyed item is rewritten when the create's
        // re-key (remapPhotoId) runs
        await offlineStorageService.savePhotoWithQueueItem(
          updatedPhoto,
          buildSyncQueueItem('update', 'photo', id, updates)
        )
      }
      return updatedPhoto
    }

    try {
      if (navigator.onLine) {
        const { data, error } = await withTimeout(
          supabase.from('photos').update(updates).eq('id', id).select().single(),
          ONLINE_REQUEST_TIMEOUT_MS,
          'photo update'
        )

        if (error) throw error
        return data
      }
    } catch (error) {
      console.error('Failed to update photo online:', error)
    }

    // If offline, update locally
    const existingPhoto = await offlineStorageService.getPhoto(id)
    if (!existingPhoto) {
      throw new Error('Photo not found')
    }

    const updatedPhoto: Photo = {
      ...existingPhoto,
      ...updates,
      updated_at: new Date().toISOString(),
    }

    // Store in IndexedDB and enqueue for sync in one transaction (REL-09)
    await offlineStorageService.savePhotoWithQueueItem(
      updatedPhoto,
      buildSyncQueueItem('update', 'photo', id, updates)
    )

    return updatedPhoto
  }

  /**
   * Delete a photo
   */
  async deletePhoto(id: string): Promise<void> {
    const queueItems = await offlineStorageService.getQueueItems()
    const photoItems = queueItems.filter((item) => item.entity === 'photo' && item.entityId === id)
    const createItem = photoItems.find((item) => item.operation === 'create')

    // The photo never reached the server and its upload hasn't started:
    // cancel the queued create (and any queued edits) and drop the local
    // copy. Once the create is in flight this is no longer safe — the
    // completing sync would resurrect the photo — so fall through and
    // queue a real delete, which the queue orders after the create.
    if (createItem && createItem.status !== 'in_progress') {
      for (const item of photoItems) {
        await offlineStorageService.deleteQueueItem(item.id)
      }
      await offlineStorageService.deletePhoto(id)
      return
    }
    if (id.startsWith('temp_') && !createItem) {
      // Legacy local-only leftover with no queued upload — nothing to sync
      await offlineStorageService.deletePhoto(id)
      return
    }

    // Only try a direct server delete when the row is known to exist; with
    // a create in flight (or a temp_ id) go straight to the queue so the
    // delete replays after the create lands (temp-keyed items are rewritten
    // by remapPhotoId when the create re-keys the photo)
    if (createItem || id.startsWith('temp_')) {
      await offlineStorageService.deletePhotoWithQueueItem(
        id,
        buildSyncQueueItem('delete', 'photo', id, { id })
      )
      return
    }

    try {
      if (navigator.onLine) {
        // Get photo to find its storage keys — the thumbnail is a separate
        // object and used to be left behind on every delete
        const { data: photo } = await withTimeout(
          supabase.from('photos').select('storage_path, thumbnail_path').eq('id', id).single(),
          ONLINE_REQUEST_TIMEOUT_MS,
          'photo lookup'
        )

        // Delete from storage before the row: the bucket policy reads the
        // job row via the key's first path segment, so this has to happen
        // while the photo is still resolvable
        if (photo) {
          await withTimeout(
            purgePhotoStorage(photo),
            ONLINE_REQUEST_TIMEOUT_MS,
            'photo storage delete'
          )
        }

        // Delete from database
        const { error } = await withTimeout(
          supabase.from('photos').delete().eq('id', id),
          ONLINE_REQUEST_TIMEOUT_MS,
          'photo delete'
        )
        if (error) throw error

        await offlineStorageService.deletePhoto(id)
        return
      }
    } catch (error) {
      console.error('Failed to delete photo online:', error)
    }

    // If offline, delete locally and enqueue for sync in one transaction (REL-09)
    await offlineStorageService.deletePhotoWithQueueItem(
      id,
      buildSyncQueueItem('delete', 'photo', id, { id })
    )
  }

  /**
   * Get a short-lived signed URL for a photo (bucket is private)
   */
  async getPhotoUrl(storagePath: string): Promise<string> {
    const { data, error } = await supabase.storage.from('photos').createSignedUrl(storagePath, 3600) // 1 hour expiry

    if (error) throw error
    if (!data?.signedUrl) throw new Error('Failed to create signed photo URL')

    return data.signedUrl
  }
}

// Export singleton instance
export const photoService = new PhotoService()
